import { clock, dayLabel, shortDate, ymdInHomeZone } from '../lifeos/format.js';

/** The fields both the project page and the workspace page get per meeting occurrence. */
export interface MeetingOccurrence {
  id: string;
  title: string;
  startsAt: string;
  recurrence?: string | null;
  projectId?: string | null;
}

/** One row in a meetings list: a whole series, or a one-off. */
export interface MeetingLine<M extends MeetingOccurrence = MeetingOccurrence> {
  key: string;
  /** The next occurrence (earliest in the list). */
  next: M;
  /** Every occurrence folded into this row, earliest first. */
  all: M[];
  series: boolean;
  /** 'Tuesdays 12:30 PM · next Oct 20' or 'Thu Oct 16 · 2:00 PM'. */
  when: string;
}

const WEEKDAYS = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];

function weekdayIndex(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay();
}

/**
 * Folds meeting occurrences into one row per series: a recurring row (same
 * event id) or the same title at the same weekday + time seen more than once
 * (a synced calendar's expanded instances) becomes "Tuesdays 12:30 PM · next
 * Oct 20"; anything else stays a dated one-off. Rows come out by next occurrence.
 */
export function groupMeetings<M extends MeetingOccurrence>(items: readonly M[], today = ymdInHomeZone(new Date())): MeetingLine<M>[] {
  const groups = new Map<string, M[]>();
  for (const m of items) {
    const ymd = ymdInHomeZone(new Date(m.startsAt));
    const recurring = !!m.recurrence && m.recurrence !== 'none';
    const key = recurring ? `id:${m.id}` : `t:${m.title.trim().toLowerCase()}|${weekdayIndex(ymd)}|${clock(m.startsAt)}`;
    const g = groups.get(key);
    if (g) g.push(m);
    else groups.set(key, [m]);
  }
  const lines = [...groups.entries()].map(([key, all]) => {
    all.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    const next = all[0]!;
    const ymd = ymdInHomeZone(new Date(next.startsAt));
    const time = clock(next.startsAt);
    const series = (!!next.recurrence && next.recurrence !== 'none') || all.length > 1;
    let when: string;
    if (!series) when = `${dayLabel(ymd)} · ${time}`;
    else {
      const cadence = next.recurrence === 'monthly' ? `Monthly, ${time}` : `${WEEKDAYS[weekdayIndex(ymd)]} ${time}`;
      when = `${cadence} · next ${ymd === today ? 'today' : shortDate(ymd, today)}`;
    }
    return { key, next, all, series, when };
  });
  return lines.sort((a, b) => a.next.startsAt.localeCompare(b.next.startsAt));
}
